import { useEffect, useRef, useState, type ReactNode } from "react";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { formatArabicNumber } from "@/lib/formatters";

interface PermissionPanelShellProps {
  dirtyCount: number;
  isPending: boolean;
  onSave: () => void;
  saveLabel: string;
  children: ReactNode;
}

/**
 * Wraps a permission editor and renders a save bar pinned to the bottom of the
 * viewport. The admin layout scrolls the window (not an inner container), so a
 * `position: sticky` bar can't pin — we use `position: fixed`, measuring the content
 * column so the bar aligns with it (and clears the sidebar), and an IntersectionObserver
 * so only the on-screen panel's bar shows (the role tab renders two panels at once).
 */
export function PermissionPanelShell({
  dirtyCount,
  isPending,
  onSave,
  saveLabel,
  children,
}: PermissionPanelShellProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);
  const [inView, setInView] = useState(true);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      setBox({ left: rect.left, width: rect.width });
    };
    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);
    window.addEventListener("resize", measure);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "0px 0px -88px 0px" },
    );
    intersectionObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const hasChanges = dirtyCount > 0;

  return (
    <div ref={rootRef} className="space-y-4 pb-24">
      {children}
      {inView && (
        <div
          className="fixed bottom-4 z-30 flex items-center justify-between gap-4 rounded-lg border bg-card/95 px-4 py-3 shadow-lg backdrop-blur"
          style={
            box
              ? { left: box.left + 12, width: box.width - 24 }
              : { left: 12, right: 12 }
          }
        >
          <p className="text-sm text-muted-foreground">
            {hasChanges
              ? `${formatArabicNumber(dirtyCount)} ${dirtyCount === 1 ? "تغيير" : "تغييرات"} غير محفوظة`
              : "لا توجد تغييرات غير محفوظة"}
          </p>
          <Button
            type="button"
            onClick={onSave}
            disabled={!hasChanges || isPending}
          >
            {isPending ? (
              <Spinner className="size-4" />
            ) : (
              <Save className="size-4" />
            )}
            <span>{saveLabel}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
